import { CartItem } from "@/assets/interfaces/carts";
import AppBtn from "@/components/ui/buttons/app-btn";
import RoundActionButton from "@/components/ui/buttons/round-action-button";
import GenericSpinner from "@/components/ui/generic-spinner";
import classNames from "classnames";
import React from "react";

interface HighlightEndNoticeProps {
  cart: CartItem[];
  show: boolean;
  toggle: (show: boolean) => void;
  hasMoreHighlights?: boolean;
  replayHighlight: () => void;
  goToStore: () => void;
  loadMoreHighlights: () => void;
  openCart: () => void;
  showAllProducts: () => void;
  isLoadingNewHighlights?: boolean;
}

const HighlightEndNotice = ({
  cart,
  show,
  toggle,
  hasMoreHighlights,
  replayHighlight,
  goToStore,
  loadMoreHighlights,
  openCart,
  showAllProducts,
  isLoadingNewHighlights,
}: HighlightEndNoticeProps) => {
  const copy = hasMoreHighlights ? copyConfig.moreHighlights : copyConfig.highlightsEnded;

  return (
    <div
      className={classNames(
        "absolute inset-0 z-[50] transition-all duration-300 ease-out",
        show ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={classNames(
          "h-full w-full bg-black/70 z-[1] transition-all duration-200 ease-out",
          show ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        style={{ backdropFilter: "blur(7px)" }}
      />
      <div
        className={classNames(
          "absolute bottom-4.5 right-4.5 left-4.5 z-[10] transform origin-bottom transition-all ease-out duration-300",
          show ? "opacity-100 scale-100 translate-y-0" : "opacity-0 pointer-events-none translate-y-10 scale-80"
        )}
      >
        <RoundActionButton onClick={() => toggle(false)} className="bg-white absolute -top-11 right-0">
          {/* prettier ignore */}
          <svg width="22" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18.8125 6L6.8125 18M6.8125 6L18.8125 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </RoundActionButton>
        <div className="bg-white rounded-15 relative">
          <div className="py-10 flex flex-col items-center px-3.75">
            <figure className="h-16 w-16 rounded-full bg-primary-500 flex items-center justify-center text-white">
              {/* prettier-ignore */}
              <svg width="50%" viewBox="0 0 33 33" fill="none">
              <path d="M12.5 29.7331H20.5C22.7133 29.7331 24.5 27.9464 24.5 25.7331V7.06641C24.5 4.85307 22.7133 3.06641 20.5 3.06641H12.5C10.2867 3.06641 8.5 4.85307 8.5 7.06641V25.7331C8.5 27.9464 10.2867 29.7331 12.5 29.7331Z" fill="currentColor"/>
              <path d="M6.49935 8.30668V24.4934C6.49935 24.92 6.11268 25.2267 5.69935 25.1467C3.72602 24.7867 3.16602 23.64 3.16602 20.84V11.96C3.16602 9.16002 3.72602 8.01335 5.69935 7.65335C6.11268 7.57335 6.49935 7.88002 6.49935 8.30668Z" fill="currentColor"/>
              <path d="M29.8333 11.96V20.84C29.8333 23.64 29.2733 24.7867 27.3 25.1467C26.8867 25.2267 26.5 24.9067 26.5 24.4934V8.30668C26.5 7.88002 26.8867 7.57335 27.3 7.65335C29.2733 8.01335 29.8333 9.16002 29.8333 11.96Z" fill="currentColor"/>
            </svg>
            </figure>
            <h6 className="text-2lg font-display font-bold text-black mt-2.5 max-w-[250px] text-center">
              {copy.title}
            </h6>
            <p className="text-sm text-dark mt-2.5 text-center max-w-[300px]">{copy.description}</p>
          </div>
          <div className="p-3.75 border-t border-grey-divider flex items-center space-x-2.5">
            <AppBtn
              color="neutral"
              size="lg"
              isBlock
              className="flex-1"
              onClick={goToStore}
              disabled={isLoadingNewHighlights}
            >
              {copy.secondaryCta}
            </AppBtn>
            <AppBtn
              color="primary"
              size="lg"
              isBlock
              className="flex-1"
              onClick={hasMoreHighlights ? loadMoreHighlights : replayHighlight}
              disabled={isLoadingNewHighlights}
            >
              {isLoadingNewHighlights ? "Loading..." : copy.primaryCta}
            </AppBtn>
          </div>
        </div>
        <AppBtn color="neutral" size="lg" isBlock className="mt-2.5" onClick={openCart}>
          Your Cart
          <div className="h-4.5 w-4.5 sm:h-5 sm:w-5 rounded-full bg-primary-500 text-white text-xxs font-bold flex items-center justify-center ml-1">
            <span className="inline-block">{cart && cart.length > 0 ? cart.length : 0}</span>
          </div>
        </AppBtn>
      </div>
    </div>
  );
};

export default HighlightEndNotice;

const copyConfig = {
  moreHighlights: {
    title: "You've reached the end of this highlight.",
    description: "Want to see more? Tap More Highlights to see more highlights from our store.",
    primaryCta: "More Highlights",
    secondaryCta: "More Products",
  },
  highlightsEnded: {
    title: "You've reached the end of our highlights.",
    description: "Click the Replay button to replay this highlight, or go back to store to see more products.",
    primaryCta: "Replay",
    secondaryCta: "More Products",
  },
};
